import React, { useState, useEffect } from 'react';
import { Cloud, Key, AlertCircle, CheckCircle, ExternalLink, Save } from 'lucide-react';

const SupabaseSettings = () => {
  const [supabaseUrl, setSupabaseUrl] = useState('');
  const [supabaseKey, setSupabaseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('unconfigured'); // 'unconfigured', 'testing', 'connected', 'error'

  // Load saved values on component mount
  useEffect(() => {
    const savedUrl = localStorage.getItem('supabase_url');
    const savedKey = localStorage.getItem('supabase_anon_key');
    
    if (savedUrl && savedUrl !== 'https://your-project.supabase.co') {
      setSupabaseUrl(savedUrl);
    }
    if (savedKey && savedKey !== 'your-anon-key') {
      setSupabaseKey(savedKey);
    }
    
    // Check if we have valid configuration
    if (savedUrl && savedKey && savedUrl !== 'https://your-project.supabase.co' && savedKey !== 'your-anon-key') {
      testConnection(savedUrl, savedKey, false);
    }
  }, []);

  const testConnection = async (url, key, showMessages = true) => {
    if (!url || !key) {
      setConnectionStatus('unconfigured');
      return;
    }

    setConnectionStatus('testing');
    
    try {
      // Test the connection by making a simple request
      const response = await fetch(`${url}/rest/v1/`, {
        headers: {
          'apikey': key,
          'Authorization': `Bearer ${key}`
        }
      });
      
      if (response.ok || response.status === 404) { // 404 is fine, means the endpoint exists
        setConnectionStatus('connected');
        if (showMessages) {
          setSuccess('✅ Successfully connected to Supabase!');
          setError('');
        }
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Supabase connection test failed:', err);
      setConnectionStatus('error');
      if (showMessages) {
        setError(`❌ Connection failed: ${err.message}`);
        setSuccess('');
      }
    }
  };

  const handleSave = async () => {
    if (!supabaseUrl.trim() || !supabaseKey.trim()) {
      setError('Both URL and API key are required');
      return;
    }

    // Validate URL format
    if (!supabaseUrl.includes('supabase.co')) {
      setError('Please enter a valid Supabase URL (should contain "supabase.co")');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Test the connection first
      await testConnection(supabaseUrl.trim(), supabaseKey.trim(), true);
      
      if (connectionStatus === 'connected' || connectionStatus === 'testing') {
        // Save to localStorage
        localStorage.setItem('supabase_url', supabaseUrl.trim());
        localStorage.setItem('supabase_anon_key', supabaseKey.trim());
        
        setSuccess('✅ Supabase configuration saved! You may need to refresh the page for changes to take effect.');
        
        // Suggest page refresh
        setTimeout(() => {
          if (window.confirm('Supabase configuration updated! Would you like to refresh the page to apply changes?')) {
            window.location.reload();
          }
        }, 2000);
      }
    } catch (err) {
      setError(`Failed to save configuration: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear the Supabase configuration?')) {
      localStorage.removeItem('supabase_url');
      localStorage.removeItem('supabase_anon_key');
      setSupabaseUrl('');
      setSupabaseKey('');
      setConnectionStatus('unconfigured');
      setSuccess('Configuration cleared');
      setError('');
    }
  };


  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-400';
      case 'testing': return 'text-yellow-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return '✅ Connected';
      case 'testing': return '🔄 Testing...';
      case 'error': return '❌ Connection Failed';
      default: return '⚠️ Not Configured';
    }
  };

  return (
    <div className="bg-gray-800 border-2 border-orange-600 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Cloud className="text-orange-400" size={24} />
        <h2 className="text-xl font-bold text-orange-300">Supabase Configuration</h2>
        <span className={`text-sm font-semibold ${getStatusColor()}`}>
          {getStatusText()}
        </span>
      </div>
      
      <p className="text-orange-200 text-sm mb-4">
        Configure your Supabase project for the Survey feature authentication and database.
      </p>

      {/* Setup Instructions */}
      <div className="bg-gray-700 border border-orange-500 rounded-lg p-4 mb-4">
        <h3 className="text-orange-300 font-semibold mb-2 flex items-center gap-2">
          <Key size={16} />
          Quick Setup:
        </h3>
        <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
          <li>Go to <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 underline">supabase.com</a></li>
          <li>Create a new project or select existing one</li>
          <li>Go to Settings → API</li>
          <li>Copy your Project URL and anon public key</li>
          <li>Paste them below and click "Save Configuration"</li>
        </ol>
        <div className="mt-3 text-xs text-gray-400">
          <strong>Need help?</strong> Check the <code className="text-orange-300">SUPABASE_SETUP.md</code> file for detailed instructions.
        </div>
      </div>

      {/* Configuration Form */}
      <div className="space-y-4">
        {/* Project URL */}
        <div>
          <label className="block text-orange-300 text-sm font-bold mb-2">
            Project URL *
          </label>
          <input
            type="url"
            value={supabaseUrl}
            onChange={(e) => setSupabaseUrl(e.target.value)}
            placeholder="https://your-project-id.supabase.co"
            className="w-full px-4 py-2 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50"
          />
          <p className="text-gray-400 text-xs mt-1">
            Found in your Supabase project Settings → API
          </p>
        </div>

        {/* API Key */}
        <div>
          <label className="block text-orange-300 text-sm font-bold mb-2">
            Anon Public Key *
          </label>
          <input
            type="password"
            value={supabaseKey}
            onChange={(e) => setSupabaseKey(e.target.value)}
            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
            className="w-full px-4 py-2 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50"
          />
          <p className="text-gray-400 text-xs mt-1">
            The "anon" "public" key from your Supabase project API settings
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="p-3 bg-red-900 border border-red-600 rounded-lg flex items-start gap-2">
            <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-red-200 text-sm">{error}</div>
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-900 border border-green-600 rounded-lg flex items-start gap-2">
            <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-green-200 text-sm">{success}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={loading || !supabaseUrl.trim() || !supabaseKey.trim()}
            className="bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
          >
            <Save size={16} />
            {loading ? 'Testing Connection...' : 'Save Configuration'}
          </button>
          
          <button
            onClick={() => testConnection(supabaseUrl.trim(), supabaseKey.trim(), true)}
            disabled={loading || !supabaseUrl.trim() || !supabaseKey.trim()}
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors"
          >
            Test Connection
          </button>
          
          {(supabaseUrl || supabaseKey) && (
            <button
              onClick={handleClear}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg font-bold transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Additional Resources */}
      <div className="mt-6 p-4 bg-gray-700 border border-gray-600 rounded-lg">
        <h4 className="text-orange-300 font-semibold mb-2">Resources:</h4>
        <div className="space-y-2 text-sm">
          <a 
            href="https://supabase.com/docs/guides/getting-started" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <ExternalLink size={14} />
            Supabase Getting Started Guide
          </a>
          <a 
            href="https://supabase.com/docs/guides/auth" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <ExternalLink size={14} />
            Supabase Authentication Documentation
          </a>
        </div>
      </div>
    </div>
  );
};

export default SupabaseSettings;
